import { Lock, MapPin, ShieldCheck, UserCheck2Icon } from "lucide-react";
import React from "react";

const InfoItem = ({
  label,
  value,
  valueClass = "",
  breakWord = false,
}) => {
  return (
    <div className="border-b border-gray-100 pb-3">
      <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
        {label}
      </p>

      <p
        className={`text-sm font-medium text-gray-800 ${
          breakWord ? "break-all" : ""
        } ${valueClass}`}
      >
        {value || "-"}
      </p>
    </div>
  );
};

const DigilockerKycCard = ({ documents = [] }) => {
  if (!documents?.length) return null;

  return (
    <div className="px-3 md:px-5 mb-8 space-y-8">
      {documents.map((doc, index) => {
        const kyc =
          doc?.document_data?.data?.document?.CertificateData?.KycRes;

        const poi = kyc?.UidData?.Poi;
        const poa = kyc?.UidData?.Poa;
        const photo = kyc?.UidData?.Pht;

        return (
          <div
            key={index}
            className="bg-white border border-gray-200 rounded-3xl shadow-2xl overflow-hidden"
          >
         {/* Minimal Header */}
<div className="border-b border-gray-200 px-5 py-4">
  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
    
    {/* Left */}
    <div>
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900">
          {doc?.name}
        </h2>

        <span className="text-xs border border-gray-300 text-gray-600 px-2 py-1 rounded-md">
          {doc?.doc_type}
        </span>

        <span className="text-xs border border-green-200 bg-green-50 text-green-700 px-2 py-1 rounded-md">
          Verified
        </span>
      </div>

      <p className="text-sm text-gray-500 mt-2">
        {doc?.issuer}
      </p>
    </div>

    {/* Right */}
    <div className="text-sm text-gray-500 md:text-right">
      <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">
        Transaction ID
      </p>

      <p className="text-gray-700 font-medium break-all">
        {doc?.document_data?.transaction_id}
      </p>
    </div>
  </div>
</div>

            {/* Main Two Column Layout */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 p-4 md:p-6">
              {/* LEFT SIDE */}
              <div className="xl:col-span-7 space-y-6">
                {/* Personal Details */}
                <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-bold text-slate-800">
                      Personal Details
                    </h3>

                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-lg">
                      <UserCheck2Icon/>
                    </div>
                  </div>

                  {/* Profile Image */}
                  {photo && (
                    <div className="flex justify-center md:justify-start mb-6">
                      <img
                        src={`data:image/jpeg;base64,${photo.replace(
                          /\s/g,
                          ""
                        )}`}
                        alt="Profile"
                        className="w-32 h-32 rounded-2xl object-cover border-4 border-white shadow-xl"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <InfoItem label="Full Name" value={poi?.name} />

                    <InfoItem label="Date of Birth" value={poi?.dob} />

                    <InfoItem
                      label="Gender"
                      value={
                        poi?.gender === "M"
                          ? "Male"
                          : poi?.gender === "F"
                          ? "Female"
                          : poi?.gender
                      }
                    />

                    <InfoItem
                      label="Masked Aadhaar"
                      value={kyc?.UidData?.uid}
                    />

                    <InfoItem
                      label="KYC Status"
                      value={kyc?.ret === "Y" ? "Verified" : "Failed"}
                      valueClass={
                        kyc?.ret === "Y"
                          ? "text-emerald-600 font-semibold"
                          : "text-red-600 font-semibold"
                      }
                    />

                    <InfoItem label="Generated At" value={kyc?.ts} />

                    <InfoItem label="Valid Till" value={kyc?.ttl} />

                    <InfoItem
                      label="Transaction"
                      value={kyc?.txn}
                      breakWord
                    />
                  </div>
                </div>

                {/* Address Details */}
                <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-5 md:p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-bold text-slate-800">
                      Address Details
                    </h3>

                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center text-lg">
                      <MapPin/>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <InfoItem label="Care Of" value={poa?.co} />

                    <InfoItem label="House" value={poa?.house} />

                    <InfoItem label="Street" value={poa?.street} />

                    <InfoItem label="Locality" value={poa?.loc} />

                    <InfoItem
                      label="Village / Town"
                      value={poa?.vtc}
                    />

                    <InfoItem
                      label="Sub District"
                      value={poa?.subdist}
                    />

                    <InfoItem label="District" value={poa?.dist} />

                    <InfoItem label="State" value={poa?.state} />

                    <InfoItem label="PIN Code" value={poa?.pc} />

                    <InfoItem label="Country" value={poa?.country} />
                  </div>
                </div>

                {/* Verification Details */}
                <div className="bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-2xl p-5 md:p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-bold text-slate-800">
                      Verification Metadata
                    </h3>

                    <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center text-lg">
                      <ShieldCheck/>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <InfoItem label="Issuer" value={doc?.issuer} />

                    <InfoItem
                      label="Document Type"
                      value={doc?.doc_type}
                    />

                    <InfoItem
                      label="Request ID"
                      value={doc?.document_data?.request_id}
                      breakWord
                    />

                    <InfoItem
                      label="Reference ID"
                      value={doc?.document_data?.reference_id}
                      breakWord
                    />

                    <InfoItem
                      label="Response Code"
                      value={doc?.document_data?.data?.code}
                    />

                    <InfoItem
                      label="Message"
                      value={doc?.document_data?.data?.message}
                    />
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE - JSON */}
              <div className="xl:col-span-5">
                <div className="bg-slate-950 rounded-2xl shadow-2xl border border-slate-800 overflow-hidden sticky top-5">
                  {/* JSON Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
                    <div>
                      <h3 className="text-white font-semibold text-lg">
                        Raw JSON Data
                      </h3>

                      <p className="text-slate-400 text-xs mt-1">
                        Complete API response
                      </p>
                    </div>

                    <div className="flex gap-1">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                  </div>

                  {/* JSON Content */}
                  <div className="max-h-[1000px] overflow-auto">
                    <pre className="text-[12px] leading-6 text-green-300 p-5 whitespace-pre-wrap break-words">
                      {JSON.stringify(doc, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DigilockerKycCard;